import { computed, ref, shallowRef, watch } from 'vue';
import { isAxiosError } from 'axios';
import { useModal } from 'vue-final-modal';
import { onBeforeRouteLeave, useRoute, useRouter, type Router } from 'vue-router';
import { trainingApi, type TrainingDetail } from '@/entities/Training';
import { useCustomToast } from '@/shared/lib';
import { ConfirmCloseModal } from '@/shared/ui';
import { logNavigationFailure } from '../lib/logNavigationFailure';
import { useOnboardingForm } from './useOnboardingForm';
import { useRoleChangeForm } from './useRoleChangeForm';
import { useSgqPunctualForm } from './useSgqPunctualForm';
import { useOpenTrainingForm } from './useOpenTrainingForm';
import { useEditHomologationTraining } from './useEditHomologationTraining';

const TRAINING_LIST_ROUTE = '/treinamentos/lista-treinamentos';
const TRAINING_EDIT_ROUTE_NAME = 'TreinamentosEditar';

const CONFIRM_CLOSE_TITLE = 'Tem certeza que deseja fechar a edição do treinamento?';
const CONFIRM_CLOSE_DESCRIPTION = 'As mudanças realizadas não serão salvas.';

/**
 * Dispatcher for the edit screen: fetches the training once, then instantiates
 * only the composable matching its type with an editContext, mirroring
 * useCreateTraining.ts's per-type shape but resolved from the fetch instead
 * of a user radio pick. Aggregates canSave/saving/save and drives the
 * discard-changes guard shared by every type (AC11-AC18).
 */
export function useEditTraining() {
  const route = useRoute();
  const router = useRouter();
  const toast = useCustomToast();

  const loading = ref(true);
  const training = ref<TrainingDetail | null>(null);

  const onboarding = shallowRef<ReturnType<typeof useOnboardingForm>>();
  const roleChange = shallowRef<ReturnType<typeof useRoleChangeForm>>();
  const sgqPunctual = shallowRef<ReturnType<typeof useSgqPunctualForm>>();
  const openTraining = shallowRef<ReturnType<typeof useOpenTrainingForm>>();
  const homologation = shallowRef<ReturnType<typeof useEditHomologationTraining>>();

  /**
   * The dirty-tracking baseline. Cleared (null) to disarm the leave-guard —
   * either permanently, right before a successful save's redirect, or
   * temporarily while the discard-changes modal is already handling the
   * navigation itself.
   */
  const loadedSnapshot = ref<string | null>(null);
  /** The true post-load baseline, kept so a *failed* save can re-arm the guard accurately. */
  const initialSnapshot = ref<string | null>(null);

  let currentRequestId = 0;

  const activeForm = computed(
    () =>
      onboarding.value ?? roleChange.value ?? sgqPunctual.value ?? openTraining.value ?? homologation.value ?? null
  );

  function redirectToList(): void {
    router.push(TRAINING_LIST_ROUTE).then((navigationResult) => {
      logNavigationFailure('useEditTraining', navigationResult);
    });
  }

  function failWith(title: string, message: string): void {
    toast.showErrorToast({ title, message, type: 'error' });
    redirectToList();
  }

  function resetForms(): void {
    onboarding.value = undefined;
    roleChange.value = undefined;
    sgqPunctual.value = undefined;
    openTraining.value = undefined;
    homologation.value = undefined;
    loadedSnapshot.value = null;
    initialSnapshot.value = null;
  }

  type EditContext = { trainingId: number; initial: TrainingDetail; router: Router };

  /**
   * Instantiates the one composable matching the fetched type, and loads it.
   *
   * This only runs after the `await` in fetchTraining below, once the training's type is
   * known — by then Vue's active component instance is gone, so useRouter() inside any of
   * these composables (via useTrainingFormSubmit) would silently resolve to undefined.
   * editContext.router is `router`, captured synchronously above, threaded through so each
   * composable can use it instead of calling useRouter() itself.
   */
  async function instantiateForm(result: TrainingDetail, editContext: EditContext): Promise<void> {
    if (result.type === 'ONBOARDING') {
      onboarding.value = useOnboardingForm(editContext);
      await onboarding.value.load();
      return;
    }
    if (result.type === 'ROLE_CHANGE') {
      roleChange.value = useRoleChangeForm(editContext);
      await roleChange.value.load();
      return;
    }
    if (result.type === 'SGQ_DOCUMENT_FOCAL') {
      sgqPunctual.value = useSgqPunctualForm(editContext);
      await sgqPunctual.value.load();
      return;
    }
    if (result.type === 'OPEN_TRAINING') {
      openTraining.value = useOpenTrainingForm(undefined, editContext);
      await openTraining.value.load();
      return;
    }
    homologation.value = useEditHomologationTraining(editContext);
    await homologation.value.load();
  }

  function reportLoadError(error: unknown): void {
    if (isAxiosError(error) && error.response?.status === 404) {
      failWith('Treinamento não encontrado', 'Não foi possível carregar o treinamento. Ele pode ter sido excluído.');
      return;
    }
    failWith('Erro ao carregar treinamento', 'Não foi possível carregar o treinamento. Tente novamente mais tarde.');
  }

  async function fetchTraining(): Promise<void> {
    const requestId = ++currentRequestId;
    const trainingId = Number(route.params.id);
    if (!Number.isInteger(trainingId) || trainingId <= 0) {
      if (requestId === currentRequestId) loading.value = false;
      failWith('Treinamento não encontrado', 'O treinamento informado não é válido.');
      return;
    }

    loading.value = true;
    resetForms();

    try {
      const result = await trainingApi.getTrainingById(trainingId);
      if (requestId !== currentRequestId) return;

      training.value = result;
      await instantiateForm(result, { trainingId, initial: result, router });

      if (requestId !== currentRequestId) return;
      const snapshot = activeForm.value?.snapshot.value ?? null;
      loadedSnapshot.value = snapshot;
      initialSnapshot.value = snapshot;
    } catch (error) {
      if (requestId !== currentRequestId) return;
      training.value = null;
      reportLoadError(error);
    } finally {
      if (requestId === currentRequestId) loading.value = false;
    }
  }

  const saving = computed(() => activeForm.value?.saving.value ?? false);

  const isDirty = computed(() => {
    if (loadedSnapshot.value === null || !activeForm.value) return false;
    return activeForm.value.snapshot.value !== loadedSnapshot.value;
  });

  /**
   * Field validity alone would flip true as soon as edit mode loads, since the
   * form is pre-populated from an already-valid record — so Salvar also requires
   * isDirty once a baseline snapshot exists (loadedSnapshot === null only during
   * the initial load or the disarm-before-redirect window right after a save).
   */
  const canSave = computed(() => {
    if (!(activeForm.value?.canSave.value ?? false)) return false;
    if (loadedSnapshot.value === null) return true;
    return isDirty.value;
  });

  async function save(): Promise<void> {
    const form = activeForm.value;
    if (!form) return;

    // onSuccess fires synchronously inside submit(), strictly before its
    // router.push — so disarming there can never race the leave-guard
    // evaluating that navigation. It only ever fires on an actual success,
    // never on a validation no-op or a failed save, so `succeeded` here is
    // an exact signal (no need to infer it from router state afterwards).
    let succeeded = false;
    await form.submit(() => {
      succeeded = true;
      loadedSnapshot.value = null;
    });

    if (!succeeded) {
      loadedSnapshot.value = initialSnapshot.value;
    }
  }

  function openDiscardChangesModal(onConfirmed: () => void): void {
    const { open, close } = useModal({
      component: ConfirmCloseModal,
      attrs: {
        title: CONFIRM_CLOSE_TITLE,
        description: CONFIRM_CLOSE_DESCRIPTION,
        onClose() {
          close();
        },
        onConfirm() {
          close();
          onConfirmed();
        },
      },
    });
    open();
  }

  onBeforeRouteLeave((to, _from, next) => {
    if (!isDirty.value) {
      next();
      return;
    }
    openDiscardChangesModal(() => {
      loadedSnapshot.value = null;
      router.push(to);
    });
    next(false);
  });

  watch(
    () => route.params.id,
    () => {
      // A successful save's own redirect changes route.params.id too (it navigates
      // away from this route), which would otherwise re-trigger fetchTraining here
      // and surface a spurious "not found" toast on top of the save's success toast.
      if (route.name !== TRAINING_EDIT_ROUTE_NAME) return;
      void fetchTraining();
    },
    { immediate: true }
  );

  return {
    loading,
    training,
    onboarding,
    roleChange,
    sgqPunctual,
    openTraining,
    homologation,
    canSave,
    saving,
    save,
  };
}
